import React, { useState } from 'react';
import { Mail, MapPin, Send, Shield, Clock, Globe, CheckCircle, ArrowRight } from 'lucide-react';
import myLightRight from "../../assets/Lightning-right.svg";
import myLightLeft from "../../assets/Lightning-left.svg";
import ParticlesBackground from "@/components/features/particles/particles";

const LandingContact: React.FC = () => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        company: '',
        interest: '',
        message: ''
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsSubmitting(true);
        // Simulate API call
        setTimeout(() => {
            setIsSubmitting(false);
            setSubmitted(true);
        }, 1500);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    if (submitted) {
        return (
            <div className="relative z-10 min-h-screen bg-[#060606] text-white overflow-hidden flex items-center justify-center pt-20">
                <div className="absolute inset-0 opacity-10 bg-[linear-gradient(0deg,transparent_24%,rgba(2,223,194,.1)_25%,rgba(2,223,194,.1)_26%,transparent_27%,transparent_74%,rgba(2,223,194,.1)_75%,rgba(2,223,194,.1)_76%,transparent_77%,transparent)] bg-[size:50px_50px]" />
                <ParticlesBackground />
                <img src={myLightRight} className="absolute -top-[1rem] -right-2 z-10 opacity-40" alt="Glow" />
                <img src={myLightLeft} className="absolute -top-[1rem] -left-2 z-10 opacity-40" alt="Glow" />
                
                <div className="relative z-20 max-w-lg w-full mx-4 text-center bg-slate-900/40 backdrop-blur-xl border border-slate-800 rounded-3xl p-12 shadow-2xl">
                    <div className="w-20 h-20 bg-[#02DFC2]/10 border border-[#02DFC2]/30 rounded-full flex items-center justify-center mx-auto mb-6 shadow-lg shadow-[#02DFC2]/20">
                        <CheckCircle className="h-10 w-10 text-[#02DFC2]" />
                    </div>
                    <h1 className="text-3xl font-grotesk font-bold text-white mb-4">
                        Request Received
                    </h1>
                    <p className="text-slate-400 mb-8 leading-relaxed">
                        Thank you for reaching out to ThreatInformant. One of our intelligence specialists will contact you at <span className="text-white font-medium">{formData.email}</span> within 24 hours.
                    </p>
                    <button
                        onClick={() => window.location.href = '/'}
                        className="inline-flex items-center justify-center w-full px-6 py-3 text-sm font-semibold text-[#060606] bg-gradient-to-r from-[#52CB9E] to-[#99FFD9] rounded-xl hover:shadow-lg hover:shadow-[#52CB9E]/20 transition-all transform hover:-translate-y-0.5"
                    >
                        Return to Homepage
                        <ArrowRight className="ml-2 h-4 w-4" />
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="relative z-10 min-h-screen bg-[#060606] text-white overflow-hidden pt-24 pb-20">
            {/* Background Elements */}
            <div className="absolute inset-0 opacity-[0.07] bg-[linear-gradient(0deg,transparent_24%,rgba(2,223,194,.1)_25%,rgba(2,223,194,.1)_26%,transparent_27%,transparent_74%,rgba(2,223,194,.1)_75%,rgba(2,223,194,.1)_76%,transparent_77%,transparent)] bg-[size:50px_50px]" />
            <img src={myLightRight} className="absolute -top-[1rem] -right-2 z-10 opacity-30 object-cover" alt="Glow" />
            <img src={myLightLeft} className="absolute -top-[1rem] -left-2 z-10 opacity-30 object-cover" alt="Glow" />
            <div className="absolute inset-0 z-0"><ParticlesBackground /></div>

            <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                
                {/* Header */}
                <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
                    <div className="inline-flex items-center px-3 py-1.5 bg-[#02DFC2]/10 border border-[#02DFC2]/20 rounded-full mb-4">
                        <Shield className="h-4 w-4 text-[#02DFC2] mr-2" />
                        <span className="text-xs font-semibold tracking-wide text-[#02DFC2] uppercase">Secure Communication Channel</span>
                    </div>
                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-grotesk font-bold text-white tracking-tight">
                        Let's secure your <br className="hidden md:block"/>
                        <span className="bg-gradient-to-r from-[#52CB9E] to-[#99FFD9] bg-clip-text text-transparent">organization.</span>
                    </h1>
                    <p className="text-lg text-slate-400 mt-4">
                        Our intelligence specialists are ready to help you gain complete visibility into your dark web exposure and external attack surface.
                    </p>
                </div>

                {/* Main Contact Block */}
                <div className="bg-slate-900/40 backdrop-blur-2xl border border-slate-800 rounded-[2.5rem] shadow-2xl overflow-hidden flex flex-col lg:flex-row">
                    
                    {/* Left Side: Info Panel */}
                    <div className="lg:w-2/5 bg-black/40 p-10 lg:p-12 border-b lg:border-b-0 lg:border-r border-slate-800 flex flex-col justify-between">
                        <div>
                            <h3 className="text-2xl font-grotesk font-bold text-white mb-8">What to expect</h3>
                            
                            <div className="space-y-8">
                                <div className="flex gap-4">
                                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#02DFC2]/10 flex items-center justify-center border border-[#02DFC2]/20">
                                        <Clock className="w-5 h-5 text-[#02DFC2]" />
                                    </div>
                                    <div>
                                        <h4 className="text-white font-medium text-lg">Rapid Response</h4>
                                        <p className="text-slate-400 text-sm mt-1 leading-relaxed">Our team typically reviews and responds to all inquiries within 2-4 hours during standard business days.</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#02DFC2]/10 flex items-center justify-center border border-[#02DFC2]/20">
                                        <Globe className="w-5 h-5 text-[#02DFC2]" />
                                    </div>
                                    <div>
                                        <h4 className="text-white font-medium text-lg">Tailored Demonstration</h4>
                                        <p className="text-slate-400 text-sm mt-1 leading-relaxed">We don't do generic pitches. We'll run a preliminary scan on your domain to show you real, actionable intelligence.</p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#02DFC2]/10 flex items-center justify-center border border-[#02DFC2]/20">
                                        <Shield className="w-5 h-5 text-[#02DFC2]" />
                                    </div>
                                    <div>
                                        <h4 className="text-white font-medium text-lg">Strict Confidentiality</h4>
                                        <p className="text-slate-400 text-sm mt-1 leading-relaxed">All communications and preliminary findings are handled with military-grade encryption and strict NDAs available upon request.</p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="mt-12 pt-10 border-t border-slate-800/50">
                            <div className="flex flex-col space-y-4">
                                <a href="mailto:info@threatinformant.io" className="flex items-center text-slate-300 hover:text-[#02DFC2] transition-colors group">
                                    <Mail className="w-5 h-5 mr-3 text-slate-500 group-hover:text-[#02DFC2] transition-colors" />
                                    info@threatinformant.io
                                </a>
                                <div className="flex items-center text-slate-300">
                                    <MapPin className="w-5 h-5 mr-3 text-slate-500" />
                                    Shield Tech Hub, Kigali, Rwanda
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Right Side: The Form */}
                    <div className="lg:w-3/5 p-10 lg:p-12">
                        <form onSubmit={handleSubmit} className="space-y-6">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-slate-300">First Name <span className="text-[#02DFC2]">*</span></label>
                                    <input 
                                        type="text" 
                                        name="firstName"
                                        required
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3.5 bg-black/50 border border-slate-700/50 rounded-xl focus:ring-1 focus:ring-[#02DFC2] focus:border-[#02DFC2] text-white placeholder-slate-600 transition-all outline-none"
                                        placeholder="John"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-slate-300">Last Name <span className="text-[#02DFC2]">*</span></label>
                                    <input 
                                        type="text" 
                                        name="lastName"
                                        required
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3.5 bg-black/50 border border-slate-700/50 rounded-xl focus:ring-1 focus:ring-[#02DFC2] focus:border-[#02DFC2] text-white placeholder-slate-600 transition-all outline-none"
                                        placeholder="Doe"
                                    />
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-300">Work Email <span className="text-[#02DFC2]">*</span></label>
                                <input 
                                    type="email" 
                                    name="email"
                                    required
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3.5 bg-black/50 border border-slate-700/50 rounded-xl focus:ring-1 focus:ring-[#02DFC2] focus:border-[#02DFC2] text-white placeholder-slate-600 transition-all outline-none"
                                    placeholder="john@company.com"
                                />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-slate-300">Company</label>
                                    <input 
                                        type="text" 
                                        name="company"
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3.5 bg-black/50 border border-slate-700/50 rounded-xl focus:ring-1 focus:ring-[#02DFC2] focus:border-[#02DFC2] text-white placeholder-slate-600 transition-all outline-none"
                                        placeholder="Organization Name"
                                    />
                                </div>
                                <div className="space-y-2">
                                    <label className="text-sm font-medium text-slate-300">Primary Interest <span className="text-[#02DFC2]">*</span></label>
                                    <select 
                                        name="interest"
                                        required
                                        onChange={handleInputChange}
                                        className="w-full px-4 py-3.5 bg-black/50 border border-slate-700/50 rounded-xl focus:ring-1 focus:ring-[#02DFC2] focus:border-[#02DFC2] text-white transition-all outline-none appearance-none cursor-pointer"
                                        style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%2364748b' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: `right 1rem center`, backgroundRepeat: `no-repeat`, backgroundSize: `1.5em 1.5em` }}
                                    >
                                        <option value="" disabled selected className="bg-slate-900">Select a topic...</option>
                                        <option value="demo" className="bg-slate-900">Request a Demo</option>
                                        <option value="pricing" className="bg-slate-900">Enterprise Pricing</option>
                                        <option value="partnership" className="bg-slate-900">MSSP / Partnership</option>
                                        <option value="other" className="bg-slate-900">Other Inquiry</option>
                                    </select>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <label className="text-sm font-medium text-slate-300">Message <span className="text-[#02DFC2]">*</span></label>
                                <textarea 
                                    name="message"
                                    required
                                    rows={4}
                                    onChange={handleInputChange}
                                    className="w-full px-4 py-3.5 bg-black/50 border border-slate-700/50 rounded-xl focus:ring-1 focus:ring-[#02DFC2] focus:border-[#02DFC2] text-white placeholder-slate-600 transition-all outline-none resize-none"
                                    placeholder="How can we help secure your organization?"
                                />
                            </div>

                            <div className="pt-2">
                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className="w-full flex items-center justify-center px-6 py-4 text-sm font-bold text-[#060606] uppercase tracking-wide bg-gradient-to-r from-[#52CB9E] to-[#99FFD9] rounded-xl hover:shadow-[0_0_20px_rgba(82,203,158,0.3)] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 transform hover:-translate-y-0.5 active:translate-y-0"
                                >
                                    {isSubmitting ? (
                                        <div className="flex items-center">
                                            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#060606] mr-3"></div>
                                            Transmitting...
                                        </div>
                                    ) : (
                                        <div className="flex items-center">
                                            <Send className="h-4 w-4 mr-2" />
                                            Send Secure Message
                                        </div>
                                    )}
                                </button>
                                <p className="text-center text-xs text-slate-500 mt-4">
                                    By submitting this form, you acknowledge our <a href="/privacy" className="text-[#02DFC2] hover:underline">Privacy Policy</a>.
                                </p>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LandingContact;